import { NavLink, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Building2,
  Kanban,
  Mail,
  Phone,
  FileText,
  FileSignature,
  BarChart3,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  Package,
  MessageSquareWarning,
  UserPlus,
  Brain,
} from "lucide-react";
import { useState } from "react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";

// Navigation items ordered by sales workflow
const navigation = [
  { name: "Dashboard", href: "/app", icon: LayoutDashboard },
  { name: "Products", href: "/app/products", icon: Package },
  { name: "Leads", href: "/app/leads", icon: Users },
  { name: "Contacts", href: "/app/contacts", icon: UserCircle },
  { name: "Accounts", href: "/app/accounts", icon: Building2 },
  { name: "Outreach", href: "/app/outreach", icon: Mail },
  { name: "AI Assist", href: "/app/calls", icon: Phone },
  { name: "Deals", href: "/app/deals", icon: Kanban },
  { name: "Proposals", href: "/app/proposals", icon: FileText },
  { name: "Contracts", href: "/app/contracts", icon: FileSignature },
  { name: "Objections", href: "/app/objections", icon: MessageSquareWarning },
  { name: "Onboarding", href: "/app/customer-onboarding", icon: UserPlus },
  { name: "AI Insights", href: "/app/ai-insights", icon: Brain },
];

const bottomNav = [
  { name: "Analytics", href: "/app/analytics", icon: BarChart3 },
  { name: "Notifications", href: "/app/notifications", icon: Bell },
  { name: "Settings", href: "/app/settings", icon: Settings },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300",
        collapsed ? "w-[72px]" : "w-64"
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={aiveliqLogo} 
              alt="Aiveliq360" 
              className={cn(
                "object-contain transition-all duration-300 cursor-pointer",
                collapsed ? "h-9 w-9" : "h-10"
              )} 
            />
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground transition-colors"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === "/app"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground ai-glow-subtle"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom Navigation */}
        <div className="px-3 py-4 border-t border-sidebar-border">
          {bottomNav.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        </div>
      </div>
    </aside>
  );
}
